import styled from 'styled-components';
import palette from 'lib/styles/palette';
import { useParams, useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { CreateBtn } from 'lib/styles/icons';
import usePlaceCreator from 'hooks/place/usePlaceCreator';
import PlanUploader from './PlanUploader';
import Loading from 'components/common/Loading';
import ErrorNoti from 'components/common/ErrorNoti';
import NotiModal from 'components/common/NotiModal';

const PlaceCreator = ({ edit }) => {
  const navigate = useNavigate();
  const { id } = useParams();

  const [showPlanUploader, setShowPlanUploader] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [notiMsg, setNotiMsg] = useState('');

  const {
    getStoreQuery,
    createStoreMutation,
    updateStoreMutation,
    store,
    code,
    tel,
    space,
    addedSpace,
    errors,
    onChange,
    onSubmit,
    onUpdate,
  } = usePlaceCreator(id, setShowErrorModal, setNotiMsg);

  if (
    getStoreQuery.isLoading ||
    createStoreMutation.isLoading ||
    updateStoreMutation.isLoading
  )
    return <Loading />;

  if (
    getStoreQuery.error?.response.status === 500 ||
    createStoreMutation.error?.response.status === 500 ||
    updateStoreMutation.error?.response.status === 500
  )
    return <ErrorNoti />;

  return (
    <>
      {showErrorModal && (
        <NotiModal
          closeModal={() => setShowErrorModal(false)}
          msg={notiMsg}
          fontL
        />
      )}
      {showPlanUploader && (
        <PlanUploader
          onClose={() => setShowPlanUploader(false)}
          edit={edit}
          setShowErrorModal={setShowErrorModal}
          setNotiMsg={setNotiMsg}
        />
      )}
      <PlaceCreatorBlock>
        <div className="header">
          <div className="title">{edit ? '장소 수정' : '신규 장소 등록'}</div>
          <div className="title-name">{store?.company}</div>
        </div>
        <div className="subtitle">1. 기본 정보</div>
        <div className="wrapper">
          <div className="name">장소</div>
          <div className="value">{store?.name}</div>
        </div>
        <div className="wrapper">
          <div className="name">주소</div>
          <div className="value">{store?.address}</div>
        </div>
        <div className="wrapper">
          <div className="name">상세 주소</div>
          <div className="value">
            {store?.detailAddress ? store?.detailAddress : '미입력'}
          </div>
        </div>
        <div className="wrapper">
          <div className="name">관리 코드</div>
          <StyledInput
            type="text"
            name="code"
            value={code}
            onChange={onChange}
          />
        </div>
        {errors.code && (
          <div className="place-creator-error">{errors.code}</div>
        )}
        <div className="wrapper">
          <div className="name">대표 전화</div>
          <StyledInput type="text" name="tel" value={tel} onChange={onChange} />
        </div>
        {errors.tel && <div className="place-creator-error">{errors.tel}</div>}
        <div className="divider" />
        <div className="subtitle">2. 도면 정보</div>
        {space.length === 0 ? (
          <div className="no-plan">도면을 등록해 주세요</div>
        ) : (
          space.map((space, idx) => (
            <div className="plan-wrapper" key={`space-${idx}`}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <div>{`${space.floor} - ${space.name}`}</div>
              </div>
              <img className="plan" src={space.filePath} alt="temp" />
            </div>
          ))
        )}
        {addedSpace.length !== 0 &&
          addedSpace.map((space, idx) => (
            <div className="plan-wrapper" key={`space-${idx}`}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <div className="ellipsis">{`${space.floor} - ${space.name}`}</div>
              </div>
              <img className="plan" src={space.filePath} alt="temp" />
            </div>
          ))}
        <div className="icon-wrapper">
          <CreateBtn
            className="icon"
            onClick={() => setShowPlanUploader(true)}
          />
        </div>
        <div className="divider" />
        <div className="btn-wrapper">
          <StyledButton cancel onClick={() => navigate(-1)}>
            취소
          </StyledButton>
          <StyledButton
            onClick={edit ? onUpdate : onSubmit}
            disabled={space.length === 0}
          >
            {edit ? '완료' : '등록'}
          </StyledButton>
        </div>
      </PlaceCreatorBlock>
    </>
  );
};

export default PlaceCreator;

const PlaceCreatorBlock = styled.div`
  width: 480px;
  margin: 0 auto;
  margin-top: 2rem;
  margin-bottom: 2rem;
  display: flex;
  flex-direction: column;
  padding: 0 1rem 2rem 1rem;
  background-color: #fff;
  border-radius: 20px;
  box-shadow: 0 0 50px 0 rgba(0, 0, 0, 0.08);

  .header {
    margin-top: 30px;
    width: 100%;
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    font-weight: bold;
  }

  .title {
    font-size: 1.3rem;
  }

  .title-name {
    font-size: 1rem;
  }

  .subtitle {
    margin-top: 2rem;
    font-size: 1.1rem;
    font-weight: bold;
  }

  .wrapper {
    margin-top: 1rem;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .divider {
    margin-top: 1.5rem;
    width: 100%;
    border-bottom: 1px solid rgba(0, 0, 0, 0.1);
  }

  .icon-wrapper {
    margin-top: 1rem;
    display: flex;
    justify-content: center;
  }

  .icon {
    font-size: 20px;
    cursor: pointer;
  }

  .btn-wrapper {
    margin-top: 2rem;
    display: flex;
    justify-content: center;
  }

  .plan-wrapper {
    margin-top: 1rem;
    div {
      font-weight: bold;
    }
    img {
      margin-top: 0.5rem;
      width: 100%;
      object-fit: contain;
    }
  }

  .no-plan {
    padding-top: 1rem;
    display: flex;
    justify-content: center;
    font-weight: 500;
  }

  .place-creator-error {
    color: red;
    font-size: 12px;
    text-align: end;
  }
`;

const StyledInput = styled.input`
  border: solid 1px #dfdfdf;
  border-radius: 8px;
  background: rgba(0, 0, 0, 0.05);
  font-size: 13px;
  width: 200px;
  padding: 3px 6px;
  &::placeholder {
    color: #c6c6c6;
  }
`;

const StyledButton = styled.button`
  padding: 10px 30px;
  border: none;
  border-radius: 5px;
  background: ${props => (props.cancel ? palette.theme[0] : palette.theme[3])};
  color: #fff;
  cursor: pointer;
  &:hover {
    background: ${palette.theme[2]};
  }
  &:disabled {
    background: ${palette.theme[0]};
  }
  & + & {
    margin-left: 1rem;
  }
`;
